import React, { useCallback, useRef } from 'react';
import { Tooltip, Box, Typography, Stack, CircularProgress } from '@mui/material';
import { useHTSDescriptions } from '@/context/hts-descriptions-context';

interface HTSCodeWithTooltipProps {
  children: React.ReactElement;
  htsCode: string;
  hoverDelay?: number; // Delay in ms before fetching on hover
}

export const HTSCodeWithTooltip: React.FC<HTSCodeWithTooltipProps> = ({ 
  children, 
  htsCode,
  hoverDelay = 300
}) => {
  const { descriptions, fetchDescription } = useHTSDescriptions();
  const timeoutRef = useRef<NodeJS.Timeout>();
  
  const htsInfo = descriptions[htsCode];

  const startFetchWithDelay = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      fetchDescription(htsCode);
    }, hoverDelay);
  }, [htsCode, fetchDescription, hoverDelay]);

  const handleTooltipOpen = useCallback(() => {
    startFetchWithDelay();
  }, [startFetchWithDelay]);

  const handleTooltipClose = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  }, []);

  // Clean up timeout on unmount
  React.useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const tooltipContent = (
    <Box sx={{ maxWidth: 300, p: 1 }}>
      <Stack spacing={1.5}>
        <Typography variant="body2" fontWeight={600} color="inherit">
          HTS Code: {htsCode}
        </Typography>
        
        {htsInfo?.isLoading ? (
          <Stack direction="row" alignItems="center" spacing={1}>
            <CircularProgress size={14} sx={{ color: 'inherit' }} />
            <Typography variant="body2" color="inherit" sx={{ lineHeight: 1.4 }}>
              Loading description...
            </Typography>
          </Stack>
        ) : htsInfo?.error ? (
          <Typography variant="body2" color="inherit" sx={{ lineHeight: 1.4, color: '#ff9800' }}>
            {htsInfo.error}
          </Typography>
        ) : htsInfo?.description ? (
          <Typography variant="body2" color="inherit" sx={{ lineHeight: 1.4 }}>
            {htsInfo.description}
          </Typography>
        ) : htsInfo?.description === null ? (
          <Typography variant="body2" color="inherit" sx={{ lineHeight: 1.4, fontStyle: 'italic', color: '#ff9800' }}>
            Description is not available. Code not found.
          </Typography>
        ) : (
          <Typography variant="body2" color="inherit" sx={{ lineHeight: 1.4, fontStyle: 'italic' }}>
            Hover to load description...
          </Typography>
        )}
      </Stack>
    </Box>
  );

  return (
    <Tooltip
      title={tooltipContent}
      placement="top"
      arrow
      onOpen={handleTooltipOpen}
      onClose={handleTooltipClose}
      slotProps={{
        popper: {
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [0, -8],
              },
            },
          ],
        },
        tooltip: {
          sx: {
            bgcolor: '#1e293b',
            color: 'white',
            fontSize: '0.875rem',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
          },
        },
      }}
    >
      <Box 
        component="span" 
        sx={{ display: 'inline-block', cursor: 'pointer' }}
      >
        {children}
      </Box>
    </Tooltip>
  );
};

export default HTSCodeWithTooltip;